import { CancelationReasonCell } from '@exam-center/exam-event-examinees';
import {
  ExamEventExamineeCertificateStatus,
  type GetExamEventExamineesForMonitoringQuery,
  ProgressStatus,
  useGetExamEventExamineesForMonitoringQuery,
} from '@exam-center/graphql';
import { useParams } from '@exam-center/router';
import { EllipsisText, Text } from '@shared/components';
import { type TableBodyRow } from '@shared/table-generator';
import { formatDateTime } from '@shared/utils';
import { useMemo } from 'react';

import {
  ExamineeMonitorStatusIcon,
  ExamineeMonitorTable,
  ExamineeProgressIcon,
} from '../../components';
import { ExamineeMonitorEkycIcon } from '../../components/examinee-monitor-ekyc-icon';
import {
  EXAM_EVENT_EXAMINEE_STATUS_LABEL,
  ExamineeMonitorStatusLabel,
} from '../../components/examinee-monitor-status-label';
import { headersWithStatuses } from '../../components/examinee-monitor-table/columns';
import { useExamEventSessionExamineeSearchParams } from '../../hooks';
import { formatExamTime } from '../../utils';
import { ExamineeMonitorMoreActionMenu } from '../examinee-monitor-more-action-menu';

function generateRows(
  rows: GetExamEventExamineesForMonitoringQuery['ecGetExamEventExamineesForSession']['result'],
): TableBodyRow[] {
  return rows.map(({ examEventExaminee, examEndedAt }) => ({
    children: [
      {
        id: 'seatNumber',
        children: examEventExaminee.seatNumber?.toString() ?? '-',
      },
      {
        id: 'name',
        children: [
          examEventExaminee.examinee.title,
          examEventExaminee.examinee.firstName,
          examEventExaminee.examinee.middleName,
          examEventExaminee.examinee.lastName,
        ]
          .filter(Boolean)
          .join(' '),
      },
      { id: 'idNumber', children: examEventExaminee.examinee.idNumber },
      {
        id: 'email',
        children: <EllipsisText>{examEventExaminee.email}</EllipsisText>,
      },
      {
        id: 'ekycStatus',
        children: <ExamineeMonitorEkycIcon status={examEventExaminee.examinee.eKycStatus} />,
      },
      {
        id: 'isExamReady',
        children: (
          <ExamineeProgressIcon
            variant={examEventExaminee.isExamReady ? 'completed' : 'disabled'}
          />
        ),
      },
      {
        id: 'course',
        children: examEventExaminee.examCourse.code,
      },
      {
        id: 'status',
        children: EXAM_EVENT_EXAMINEE_STATUS_LABEL[examEventExaminee.status],
      },
      {
        id: 'remark',
        children: (
          <CancelationReasonCell
            cancelationReasonCategory={examEventExaminee.cancelationReason}
            remark={examEventExaminee.remark}
          />
        ),
      },
      {
        id: 'realtimeStatus.loginStatus',
        children: (
          <ExamineeMonitorStatusIcon
            status={
              examEventExaminee.examEventSessionExaminee?.loginStatus ?? ProgressStatus.NotStarted
            }
          />
        ),
      },
      {
        id: 'realtimeStatus.examStatus',
        children: (
          <ExamineeMonitorStatusIcon
            status={
              examEventExaminee.examEventSessionExaminee?.examStatus ?? ProgressStatus.NotStarted
            }
          />
        ),
      },
      {
        id: 'realtimeStatus.examSubmitStatus',
        children: (
          <ExamineeMonitorStatusIcon
            status={
              examEventExaminee.examEventSessionExaminee?.examSubmitStatus ??
              ProgressStatus.NotStarted
            }
          />
        ),
      },
      {
        id: 'realtimeStatus.questionnaireStatus',
        children: (
          <ExamineeMonitorStatusIcon
            status={
              examEventExaminee.examEventSessionExaminee?.questionnaireStatus ??
              ProgressStatus.NotStarted
            }
          />
        ),
      },
      {
        id: 'examEndedAt',
        align: 'center',
        children: <Text sx={{ textAlign: 'center' }}>{formatExamTime(examEndedAt)}</Text>,
      },
      {
        id: 'result',
        children: examEventExaminee.examEventExamineeResult?.result ? (
          <ExamineeMonitorStatusLabel status={examEventExaminee.examEventExamineeResult?.result} />
        ) : (
          <Text sx={{ textAlign: 'center' }}>-</Text>
        ),
      },
      {
        id: 'certificate',
        children: (
          <ExamineeProgressIcon
            variant={
              examEventExaminee.certificateStatus === ExamEventExamineeCertificateStatus.Sent
                ? 'completed'
                : 'disabled'
            }
          />
        ),
      },
      {
        id: 'sentToSETPortalAt',
        children: examEventExaminee.sentToSETPortalAt
          ? formatDateTime(examEventExaminee.sentToSETPortalAt)
          : '-',
      },
      {
        id: 'action',
        sx: (theme) => ({ padding: theme.spacing(1.5) }),
        children: (
          <ExamineeMonitorMoreActionMenu
            examEventExamineeId={examEventExaminee.id}
            examEventExamineeSeatNumber={examEventExaminee.seatNumber}
            examEventExamineeEkycStatus={examEventExaminee.examinee.eKycStatus}
            examEventExamineeStatus={examEventExaminee.status}
            certificateStatus={examEventExaminee.certificateStatus}
            examCourseCode={examEventExaminee.examCourse.code}
            examEventExamineeRegistrationStatus={examEventExaminee.registrationStatus}
            examEventSessionExamineesExamStartedAt={[
              examEventExaminee.examEventSessionExaminee?.examStartedAt ?? null,
            ]}
          />
        ),
      },
    ],
  }));
}

export default function ExamineeMonitorList({ sessionId }: { sessionId: string }) {
  const { examEventId } = useParams('/:examCenterCode/proctoring/exam-events/:examEventId/details');

  const [{ search }] = useExamEventSessionExamineeSearchParams();

  const { data, loading: isLoading } = useGetExamEventExamineesForMonitoringQuery({
    variables: {
      sessionId,
      examEventId: examEventId,
      searchExaminee: {
        filter: {
          searchText: {
            fields: ['fullName'],
            value: search || '',
          },
        },
      },
      paging: null,
    },
    pollInterval: 10000,
  });

  const examEventExaminees = useMemo(
    () => data?.ecGetExamEventExamineesForSession.result ?? [],
    [data?.ecGetExamEventExamineesForSession],
  );

  const isInitialLoading = isLoading && !data;
  const rows = useMemo(() => generateRows(examEventExaminees), [examEventExaminees]);

  return (
    <ExamineeMonitorTable
      rows={rows}
      columns={headersWithStatuses}
      isLoading={isInitialLoading}
      slotProps={{
        tableHeaderCell: {
          sx: (theme) => ({
            p: theme.spacing(3),
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
            borderRight: `1px solid ${theme.palette.grey[300]}`,
            backgroundColor: theme.palette.grey[100],
            ...theme.typography.body1Semibold,
          }),
        },
        tableBodyRow: {
          sx: {
            '&:last-child td': {
              borderBottom: 'none',
            },
          },
        },
        tableBodyCell: {
          sx: (theme) => ({
            p: theme.spacing(2, 3),
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
            borderRight: `1px solid ${theme.palette.grey[300]}`,
            ...theme.typography.body1,
            '&:last-child': {
              borderRight: 'none',
            },
            '&:nth-of-type(1), &:nth-of-type(n+10):nth-of-type(-n+13)': {
              textAlign: 'center',
            },
          }),
        },
      }}
    />
  );
}
